import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { httpStatusOf } from '../../../utils/requestHandling';
import {
  getGoalById,
  createGoal,
  updateGoal,
  deleteGoal,
  updateGoalProgress,
  getGoalStats,
  getUserCompanyDepartments,
  getAvailableOwners,
  getGoalTree,
  isAbortError,
  GoalFilterOptions,
  GoalStatsView,
  GoalOwnerView,
  DepartmentBasicView,
  GoalTreeView,
} from '../../../api/PreformanceManagement/Goals';

import type { Goal, Filters, ExpandedSections, Position } from './types';
import {
  convertGoalViewToGoal,
  convertGoalToGoalInput,
  mapBackendLevelToFrontend,
} from './utils';

const SEARCH_DEBOUNCE_MS = 300;

// Layout rhythm for the map. The row gap is derived from the parent/child gap so wrapped rows sit
// tighter than the separation between levels. The node sizes must stay identical to the ones
// GoalNode's getLevelStyles() actually renders, or the reserved box is smaller than the node and
// the gaps disappear.
const NODE_WIDTHS = { company: 360, team: 320, individual: 280 };
const NODE_HEIGHTS = { company: 120, team: 100, individual: 88 };
const H_GAP = 65;
const V_GAP = 108;
const ROW_GAP = Math.round(V_GAP / 2);
// Separation between the stacked root bands, wider than V_GAP so the bands read as distinct levels
// rather than as one parent's children.
const BAND_GAP = 162;
const BASE_TOP = 40;
// A row never runs past this many nodes even when the canvas is wide enough for more.
const MAX_PER_ROW = 10;
const MIN_CONTAINER_WIDTH = 640;
const CHILDREN_PER_PARENT = 12;
const ROOT_PARENT_KEY = 'root';

type GoalLevel = Goal['level'];

const LEVEL_ORDER: GoalLevel[] = ['company', 'team', 'individual'];

export interface ShowMoreTile {
  parentId: string;
  remaining: number;
}

interface MeasuredNode {
  id: string;
  level: GoalLevel;
  width: number;
  height: number;
  block: MeasuredBlock | null;
}

interface BlockEntry {
  measured: MeasuredNode | null;
  width: number;
  height: number;
}

interface MeasuredBlock {
  parentId: string;
  entries: BlockEntry[];
  rows: number[][];
  rowWidths: number[];
  rowHeights: number[];
  width: number;
  height: number;
  remaining: number;
}

// CO-577 — `err.response.data.errors[].message` modelled a shape the server never sends and the
// interceptor never produces. The api layer now throws an Error whose `message` already carries the
// server's own refusal text, so reading `.message` is the whole job.
function getErrorMessage(err: unknown, fallback: string): string {
  return (err instanceof Error ? err.message : '') || fallback;
}

const dateOnly = (value?: string): string => (value ? value.split('T')[0] : '');

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// FilterPanel emits the department id (falling back to a lowercased name when a department has no
// id), so the goal is matched on id, not on the display name.
const matchesDepartment = (departmentId: number | undefined, departmentName: string | undefined, selected: string): boolean => {
  const id = departmentId != null ? String(departmentId) : '';
  return id === selected || (departmentName || '').toLowerCase() === selected;
};

// Parsed part by part so a YYYY-MM-DD string is never shifted a day by UTC conversion.
const parseLocalDate = (value?: string): Date | null => {
  const parts = dateOnly(value).split('-').map(Number);
  if (parts.length !== 3 || parts.some(part => !Number.isFinite(part) || part === 0)) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

const matchesDueDate = (dueDate: string, mode: string): boolean => {
  if (!mode || mode === 'all') return true;
  const due = parseLocalDate(dueDate);
  if (!due) return false;
  const today = startOfDay(new Date());

  switch (mode) {
    case 'overdue':
      return due.getTime() < today.getTime();
    case 'this-week': {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 7);
      return due >= weekStart && due < weekEnd;
    }
    case 'this-month':
      return due.getFullYear() === today.getFullYear() && due.getMonth() === today.getMonth();
    case 'next-quarter': {
      const quarterStart = new Date(today.getFullYear(), (Math.floor(today.getMonth() / 3) + 1) * 3, 1);
      const quarterEnd = new Date(quarterStart.getFullYear(), quarterStart.getMonth() + 3, 1);
      return due >= quarterStart && due < quarterEnd;
    }
    case 'current-year':
      return due.getFullYear() === today.getFullYear();
    default:
      return true;
  }
};

// The single client-side filter predicate. The list and the map both run it, so a filter can never
// mean one thing in the list and another in the layout.
const matchesFilters = (goal: Goal, filters: Filters): boolean => {
  if (filters.search &&
      !goal.title.toLowerCase().includes(filters.search.toLowerCase()) &&
      !goal.description.toLowerCase().includes(filters.search.toLowerCase())) {
    return false;
  }
  if (filters.department && filters.department !== 'all' && !matchesDepartment(goal.departmentId, goal.department, filters.department)) {
    return false;
  }
  if (filters.user && filters.user !== 'all' && goal.owner !== filters.user) {
    return false;
  }
  if (filters.status && filters.status.length > 0 && !filters.status.includes(goal.status)) {
    return false;
  }
  if (filters.levels && filters.levels.length > 0 && !filters.levels.includes(goal.level)) {
    return false;
  }
  if (filters.hasBlockers && !goal.hasActiveBlockers) {
    return false;
  }
  if (filters.unclearOnly && !(goal.clarityScore == null || goal.clarityScore < 50)) {
    return false;
  }
  if (!matchesDueDate(goal.dueDate, filters.dueDate)) {
    return false;
  }
  return true;
};

const hasActiveFilters = (filters: Filters): boolean => (
  Boolean(filters.search)
  || (Boolean(filters.department) && filters.department !== 'all')
  || (Boolean(filters.user) && filters.user !== 'all')
  || (Boolean(filters.dueDate) && filters.dueDate !== 'all')
  || filters.status.length > 0
  || filters.levels.length > 0
  || (filters.priority?.length ?? 0) > 0
  || (filters.tags?.length ?? 0) > 0
  || Boolean(filters.hasBlockers)
  || Boolean(filters.unclearOnly)
);

// Greedy row packing, shared by the measuring and the positioning pass so both always agree on how
// many children sit on a row.
const packRows = (entries: BlockEntry[], available: number) => {
  const rows: number[][] = [];
  const rowWidths: number[] = [];
  const rowHeights: number[] = [];
  let current: number[] = [];
  let currentWidth = 0;
  let currentHeight = 0;

  const flush = () => {
    if (current.length === 0) return;
    rows.push(current);
    rowWidths.push(currentWidth);
    rowHeights.push(currentHeight);
    current = [];
    currentWidth = 0;
    currentHeight = 0;
  };

  entries.forEach((entry, index) => {
    const nextWidth = current.length === 0 ? entry.width : currentWidth + H_GAP + entry.width;
    if (current.length > 0 && (current.length >= MAX_PER_ROW || nextWidth > available)) {
      flush();
      current = [index];
      currentWidth = entry.width;
      currentHeight = entry.height;
      return;
    }
    current.push(index);
    currentWidth = nextWidth;
    currentHeight = Math.max(currentHeight, entry.height);
  });
  flush();

  return {
    rows,
    rowWidths,
    rowHeights,
    width: rowWidths.length > 0 ? Math.max(...rowWidths) : 0,
    height: rowHeights.reduce((sum, h) => sum + h, 0) + Math.max(0, rowHeights.length - 1) * ROW_GAP,
  };
};

export function useGoals(userId: string | undefined, t: (key: string, options?: Record<string, unknown>) => string) {
  // Add fallback for userId using localStorage
  const currentUserId = userId || localStorage.getItem('currentUserId') || '';

  // Save userId to localStorage when it's available
  useEffect(() => {
    if (userId) {
      localStorage.setItem('currentUserId', userId);
    }
  }, [userId]);

  // ----------------------
  // STATE
  // ----------------------
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean>(true);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<'view' | 'create' | 'edit'>('view');
  const [hoveredGoal, setHoveredGoal] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>({
    company: true,
    team: true,
    individual: true
  });
  const [viewType, setViewType] = useState<'map' | 'list'>('map');
  // The Q1 "goal clarity" drill-down links here with ?filter=needsClarity so the user lands
  // on the goals the score is about, instead of every goal in the company. Read once, at
  // initialisation, so a later manual change to the filter is never overridden.
  const [filters, setFilters] = useState<Filters>(() => {
    const needsClarity = new URLSearchParams(window.location.search).get('filter') === 'needsClarity';
    return {
      search: '',
      department: 'all',
      user: 'all',
      dueDate: 'all',
      status: [],
      levels: [],
      unclearOnly: needsClarity || undefined,
    };
  });

  // Read through a ref inside effects: i18next swaps `t` when translations load, which would
  // otherwise abort and re-issue the whole goals+tree+stats triple on mount.
  const tRef = useRef(t);
  tRef.current = t;

  // API-related state
  const [treeData, setTreeData] = useState<GoalTreeView | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(false);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [savingGoal, setSavingGoal] = useState<boolean>(false);
  const [stats, setStats] = useState<GoalStatsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);

  // Departments & owners state
  const [departments, setDepartments] = useState<DepartmentBasicView[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState<boolean>(false);
  const [availableOwners, setAvailableOwners] = useState<GoalOwnerView[]>([]);
  const [loadingOwners, setLoadingOwners] = useState<boolean>(false);

  // How many children each parent shows on the map, raised in steps by "show more".
  const [childLimits, setChildLimits] = useState<Record<string, number>>({});

  // Only the text search is debounced; every other filter refetches immediately.
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  // ----------------------
  // EFFECTS
  // ----------------------

  // Fetch departments and available owners on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const depts = await getUserCompanyDepartments();
        setDepartments(depts || []);
      } catch (error) {
        console.error('Failed to fetch departments:', error);
        setDepartments([]);
      } finally {
        setLoadingDepartments(false);
      }
    };

    const fetchAvailableOwners = async () => {
      try {
        setLoadingOwners(true);
        const owners = await getAvailableOwners();
        setAvailableOwners(owners || []);
      } catch (error) {
        console.error('Failed to fetch available owners:', error);
        setAvailableOwners([]);
      } finally {
        setLoadingOwners(false);
      }
    };

    fetchDepartments();
    fetchAvailableOwners();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Only what the server can actually bind. GoalFilterInput takes a SINGLE Level/Status enum and has
  // no priority, tags or department field, so a comma-joined multi-select 400s the request. Those
  // four stay client-side until the server accepts multi-select.
  const filterOptions = useMemo<GoalFilterOptions>(() => ({
    searchTerm: debouncedSearch || undefined,
    onlyUnclear: filters.unclearOnly || undefined,
  }), [debouncedSearch, filters.unclearOnly]);

  // The tree is the only source of goals now, so its failure is the page's failure.
  useEffect(() => {
    const controller = new AbortController();

    const fetchGoals = async () => {
      setLoading(true);
      setError(null);
      try {
        const [treeResult, statsResult] = await Promise.allSettled([
          getGoalTree(undefined, controller.signal),
          getGoalStats(undefined, controller.signal),
        ]);

        // A cancelled run must never clear the list or raise a toast: a newer run owns the state.
        if (controller.signal.aborted) return;

        if (treeResult.status === 'fulfilled' && treeResult.value) {
          const tree = treeResult.value;
          setTreeData(tree);
          // Auto-expand company-level goals on first load only.
          setExpandedNodes(prev => {
            if (prev.size > 0 || !tree.children?.length) return prev;
            const initial = new Set<string>();
            tree.children.forEach(child => {
              if (child.goal?.id) initial.add(child.goal.id);
            });
            return initial.size > 0 ? initial : prev;
          });
        } else if (treeResult.status === 'rejected' && !isAbortError(treeResult.reason)) {
          // treeData is deliberately left untouched, and the two cases differ: on first load it is
          // already null, so the page still shows the empty state plus this error, while on a later
          // refresh it holds the last good tree. Keeping it means a transient failure after a save
          // cannot read as though the save wiped the map.
          const status = httpStatusOf(treeResult.reason);
          if (status === 401) {
            setError(tRef.current('Authentication required. Please log in.'));
          } else if (status === 403) {
            setError(tRef.current('You do not have permission to view goals.'));
          } else {
            setError(tRef.current('Could not fetch goals from server.'));
          }
        } else if (treeResult.status === 'fulfilled') {
          setError(tRef.current('Could not fetch goals from server.'));
        }

        // A null `stats` is the signal for summaryStats to fall back to the local numbers.
        setStats(statsResult.status === 'fulfilled' ? statsResult.value : null);
      } catch (err) {
        if (isAbortError(err) || controller.signal.aborted) return;
        console.error('Error in fetchGoals:', err);
        setError(getErrorMessage(err, tRef.current('An error occurred while fetching goals.')));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchGoals();
    return () => controller.abort();
  }, [filterOptions, refetchTrigger]);

  // ----------------------
  // HELPERS
  // ----------------------

  // Helper: toggle expand/collapse for tree node
  const toggleNodeExpanded = (goalId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(goalId)) {
        next.delete(goalId);
      } else {
        next.add(goalId);
      }
      return next;
    });
  };

  const showMoreChildren = useCallback((parentId: string) => {
    setChildLimits(prev => ({
      ...prev,
      [parentId]: (prev[parentId] ?? CHILDREN_PER_PARENT) + CHILDREN_PER_PARENT,
    }));
  }, []);

  const applyStatusFilter = useCallback((status: Goal['status']) => {
    setFilters(prev => ({ ...prev, status: [status] }));
  }, []);

  // ----------------------
  // COMPUTED
  // ----------------------

  // Every goal in the company, flattened from the unpaged tree. This is the only source of goals:
  // a failed tree fetch leaves it empty and the error toast explains why.
  const allGoalsFlat = useMemo<Goal[]>(() => {
    if (!treeData) return [];
    const flat: Goal[] = [];
    const seen = new Set<string>();
    const walk = (node: GoalTreeView) => {
      const goal = node.goal;
      if (goal?.id && goal.id !== 'virtual-root' && !seen.has(goal.id)) {
        seen.add(goal.id);
        flat.push(convertGoalViewToGoal(goal));
      }
      node.children?.forEach(walk);
    };
    walk(treeData);
    return flat;
  }, [treeData]);

  const mapGoals = useMemo(
    () => allGoalsFlat.filter(goal => matchesFilters(goal, filters)),
    [allGoalsFlat, filters],
  );

  // The layout asks this instead of re-running the predicate on a GoalView, so the nodes it
  // positions are exactly the ones index.tsx renders.
  const visibleMapIds = useMemo(() => new Set(mapGoals.map(g => g.id)), [mapGoals]);

  const filtersActive = useMemo(() => hasActiveFilters(filters), [filters]);

  const summaryStats = useMemo(() => {
    // Never mix scopes in one card: the server stats ignore filters, so an active filter means all
    // five numbers come from the filtered set.
    if (!filtersActive && stats) {
      return {
        total: stats.totalGoals ?? 0,
        onTrack: stats.onTrackGoals ?? 0,
        atRisk: stats.atRiskGoals ?? 0,
        completed: stats.completedGoals ?? 0,
        averageProgress: stats.averageProgress ?? 0,
      };
    }
    const total = mapGoals.length;
    return {
      total,
      onTrack: mapGoals.filter(g => g.status === 'on-track').length,
      atRisk: mapGoals.filter(g => g.status === 'at-risk').length,
      completed: mapGoals.filter(g => g.status === 'completed').length,
      averageProgress: total > 0
        ? Math.round(mapGoals.reduce((sum, g) => sum + g.progress, 0) / total)
        : 0,
    };
  }, [stats, filtersActive, mapGoals]);

  // ----------------------
  // TREE POSITION HELPERS
  // ----------------------

  // Build tree position map: calculates x/y positions for hierarchical tree layout. `topInset` is
  // the space the floating summary panel occupies; reserving it here rather than as CSS padding
  // keeps the nodes and the SVG connectors in one coordinate space.
  const buildTreePositionMap = useCallback((containerWidth: number, topInset = 0): {
    posMap: Map<string, Position>;
    showMoreTiles: Map<string, ShowMoreTile>;
  } => {
    const posMap = new Map<string, Position>();
    const showMoreTiles = new Map<string, ShowMoreTile>();
    const width = Math.max(containerWidth || 0, MIN_CONTAINER_WIDTH);
    const available = width - 80;
    const topY = Math.max(BASE_TOP, topInset);

    // Flat level-band fallback. It fires when there is no tree at all, and ALSO whenever a filter is
    // active: a filtered view is not a hierarchy, so tree layout cannot place a match whose ancestor
    // chain did not also match. Seeding layout from the matching top-level company children only
    // (see below) leaves the canvas blank whenever every match is a team/individual goal, or sits
    // under a collapsed parent, while summaryStats still reports mapGoals.length > 0. (CO-524)
    if (!treeData?.children?.length || filtersActive) {
      // Fallback: use flat goals grouped by level
      const spacing = H_GAP;
      let baseY = topY;

      for (const level of LEVEL_ORDER) {
        const levelGoals = mapGoals.filter(g => g.level === level);
        if (levelGoals.length === 0) continue;
        const nodeWidth = NODE_WIDTHS[level];
        const nodeHeight = NODE_HEIGHTS[level];
        const perRow = Math.max(1, Math.min(MAX_PER_ROW, Math.floor((available + spacing) / (nodeWidth + spacing))));
        const rowStep = nodeHeight + ROW_GAP;

        levelGoals.forEach((goal, idx) => {
          const row = Math.floor(idx / perRow);
          const col = idx % perRow;
          const goalsInRow = Math.min(perRow, levelGoals.length - row * perRow);
          const totalW = goalsInRow * nodeWidth + (goalsInRow - 1) * spacing;
          const startX = Math.max(20, (width - totalW) / 2);
          posMap.set(goal.id, {
            x: startX + col * (nodeWidth + spacing),
            y: baseY + row * rowStep,
            width: nodeWidth,
            height: nodeHeight,
          });
        });

        // Each level starts below the rows the previous one actually needed.
        baseY += Math.ceil(levelGoals.length / perRow) * rowStep + BAND_GAP;
      }
      return { posMap, showMoreTiles };
    }

    // Measure a node together with the wrapped block of children beneath it.
    const measure = (node: GoalTreeView, visited: Set<string>): MeasuredNode | null => {
      const goal = node.goal;
      if (!goal?.id) return null;
      const level = mapBackendLevelToFrontend(goal.level as string | number);
      const w = NODE_WIDTHS[level];
      const h = NODE_HEIGHTS[level];
      const block = measureChildren(node, goal.id, visited);
      return {
        id: goal.id,
        level,
        width: block ? Math.max(w, block.width) : w,
        height: block ? h + V_GAP + block.height : h,
        block,
      };
    };

    const measureChildren = (node: GoalTreeView, parentId: string, visited: Set<string>): MeasuredBlock | null => {
      if (!expandedNodes.has(parentId) || !node.children?.length) return null;
      // The ancestor chain travels down so a circular parentId cannot recurse forever.
      const nextVisited = new Set(visited);
      nextVisited.add(parentId);
      const visible = node.children.filter(c => c.goal?.id && !nextVisited.has(c.goal.id) && visibleMapIds.has(c.goal.id));
      if (visible.length === 0) return null;
      return buildBlock(visible, parentId, nextVisited);
    };

    // Packs an already-capped list. `remaining` drives the overflow tile, so a caller that splits
    // one parent's children across several blocks can still emit a single tile.
    const packBlock = (shown: GoalTreeView[], parentId: string, visited: Set<string>, remaining: number): MeasuredBlock | null => {
      const entries: BlockEntry[] = [];
      shown.forEach(child => {
        const measured = measure(child, visited);
        if (measured) entries.push({ measured, width: measured.width, height: measured.height });
      });
      if (entries.length === 0) return null;

      if (remaining > 0) {
        const tileLevel = mapBackendLevelToFrontend(shown[0]?.goal?.level as string | number);
        entries.push({ measured: null, width: NODE_WIDTHS[tileLevel], height: NODE_HEIGHTS[tileLevel] });
      }

      return { parentId, entries, remaining, ...packRows(entries, available) };
    };

    const buildBlock = (children: GoalTreeView[], parentId: string, visited: Set<string>): MeasuredBlock | null => {
      const limit = childLimits[parentId] ?? CHILDREN_PER_PARENT;
      const shown = children.slice(0, limit);
      return packBlock(shown, parentId, visited, children.length - shown.length);
    };

    const placeNode = (measured: MeasuredNode, centerX: number, y: number) => {
      const w = NODE_WIDTHS[measured.level];
      const h = NODE_HEIGHTS[measured.level];
      posMap.set(measured.id, { x: centerX - w / 2, y, width: w, height: h });
      if (measured.block) placeBlock(measured.block, centerX, y + h + V_GAP);
    };

    const placeBlock = (block: MeasuredBlock, centerX: number, startY: number) => {
      let rowY = startY;
      block.rows.forEach((row, rowIndex) => {
        let x = centerX - block.rowWidths[rowIndex] / 2;
        row.forEach(entryIndex => {
          const entry = block.entries[entryIndex];
          const entryCenter = x + entry.width / 2;
          if (entry.measured) {
            placeNode(entry.measured, entryCenter, rowY);
          } else {
            const key = `showmore:${block.parentId}`;
            posMap.set(key, {
              x: entryCenter - entry.width / 2,
              y: rowY,
              width: entry.width,
              height: entry.height,
            });
            showMoreTiles.set(key, { parentId: block.parentId, remaining: block.remaining });
          }
          x += entry.width + H_GAP;
        });
        rowY += block.rowHeights[rowIndex] + ROW_GAP;
      });
    };

    const topChildren = treeData.children.filter(c =>
      c.goal?.id && c.goal.id !== 'virtual-root' && visibleMapIds.has(c.goal.id)
    );

    // Roots are everything without a usable parent, so an orphaned team or individual goal lands
    // here beside the real company goals. Stacking them into per-level bands keeps the hierarchy
    // readable; the cap is applied once over the whole root list so there is still one overflow tile.
    const rootLimit = childLimits[ROOT_PARENT_KEY] ?? CHILDREN_PER_PARENT;
    const shownRoots = topChildren.slice(0, rootLimit);
    const rootRemaining = topChildren.length - shownRoots.length;
    const bands = LEVEL_ORDER
      .map(level => shownRoots.filter(c => mapBackendLevelToFrontend(c.goal?.level as string | number) === level))
      .filter(band => band.length > 0);

    let bandY = topY;
    bands.forEach((band, index) => {
      const isLastBand = index === bands.length - 1;
      const block = packBlock(band, ROOT_PARENT_KEY, new Set<string>(), isLastBand ? rootRemaining : 0);
      if (!block) return;
      placeBlock(block, Math.max(40 + block.width / 2, width / 2), bandY);
      bandY += block.height + BAND_GAP;
    });

    return { posMap, showMoreTiles };
  }, [treeData, mapGoals, expandedNodes, childLimits, visibleMapIds, filtersActive]);

  // ----------------------
  // EVENT HANDLERS
  // ----------------------

  // The modal opens on the row we already have, so the map is never torn down; the authoritative
  // record replaces it when the detail fetch lands.
  const handleGoalClick = async (goal: Goal): Promise<void> => {
    setSelectedGoal(goal);
    setModalMode('view');
    setIsModalOpen(true);
    setDetailLoading(true);
    try {
      const goalDetails = await getGoalById(goal.id);
      setSelectedGoal(convertGoalViewToGoal(goalDetails));
    } catch (err) {
      console.error('Could not fetch goal details, using existing data:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * Opens a goal from its id alone. `handleGoalClick` needs a loaded row, which a deep link cannot
   * promise: the linked goal may belong to another employee, sit on a later page, or be filtered
   * out entirely. There is no row to show first, so the modal opens empty and renders its own
   * skeleton while `detailLoading` is true.
   */
  const openGoalById = useCallback(async (goalId: string): Promise<boolean> => {
    setSelectedGoal(null);
    setModalMode('view');
    setIsModalOpen(true);
    setDetailLoading(true);
    try {
      const goalDetails = await getGoalById(goalId);
      setSelectedGoal(convertGoalViewToGoal(goalDetails));
      return true;
    } catch (err) {
      console.error('Could not open the linked goal:', err);
      setIsModalOpen(false);
      return false;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleCreateNewGoal = (): void => {
    setSelectedGoal(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleSaveGoal = async (goalData: Goal, isNew: boolean): Promise<void> => {
    try {
      setSavingGoal(true);
      setError(null);

      // Use multiple fallbacks for userId
      const effectiveUserId = goalData.ownerId || currentUserId || userId || localStorage.getItem('currentUserId');
      if (!effectiveUserId) {
        // THROW, do not return. A bare return resolved the promise, so the modal treated a failed
        // save as a success: it closed and discarded everything the user had typed, while the only
        // explanation went to a toast behind it.
        console.error('User ID not found in any source!');
        throw new Error(t('User session expired. Please refresh the page and try again.'));
      }

      const goalInput = convertGoalToGoalInput(goalData, effectiveUserId);

      if (isNew) {
        try {
          await createGoal(goalInput);
          setSuccessMessage(t('Goal created successfully!'));
          setIsModalOpen(false);
          setSelectedGoal(null);
        } catch (createError) {
          console.error('Failed to create goal:', createError instanceof Error ? createError.message : createError);

          // The dialog renders this itself now, next to the form that was refused. Setting the page
          // toast as well produced the same sentence twice, once where it could not be read.
          throw createError;
        }
      } else {
        if (!goalData.id) {
          throw new Error(t('Goal ID is required for update'));
        }
        try {
          const updatedGoal = await updateGoal(goalData.id, goalInput);
          setSelectedGoal(convertGoalViewToGoal(updatedGoal));
          setSuccessMessage(t('Goal updated successfully!'));
          setIsModalOpen(false);
        } catch (updateError) {
          console.error('Failed to update goal:', updateError instanceof Error ? updateError.message : updateError);
          throw updateError;
        }
      }

      // Trigger refetch to get fresh data from server
      setRefetchTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error saving goal:', err);
      // NO setError here. The page toast renders at the foot of the page, BEHIND the open dialog,
      // so a save refusal shown there was unreadable in the one situation it mattered. The dialog
      // now renders the refusal itself, at the top of its own body, from the error rethrown below.
      // The toast is still right for page-level failures (list fetch, delete, progress), which have
      // no open form to attach to.
      // RETHROW. Swallowing here made `onSaveGoal` resolve on failure, so GoalDetailModal's
      // `catch { /* Don't close modal on error */ }` could never fire: it closed the dialog and threw
      // away everything the user had typed, leaving only an error banner on the page behind it.
      // The only caller is GoalDetailModal.handleSave, which catches, so nothing else is exposed.
      throw err;
    } finally {
      setSavingGoal(false);
    }
  };

  const handleDeleteGoal = async (goalId: string): Promise<void> => {
    try {
      setSavingGoal(true);
      const success = await deleteGoal(goalId);
      if (!success) {
        throw new Error(t('Failed to delete goal'));
      }
      setSuccessMessage(t('Goal deleted successfully!'));

      setIsModalOpen(false);
      setSelectedGoal(null);
      setRefetchTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error deleting goal:', err);
      setError(getErrorMessage(err, t('Failed to delete goal')));
      throw err;
    } finally {
      setSavingGoal(false);
    }
  };

  const handleUpdateProgress = async (goalId: string, progress: number, notes?: string): Promise<void> => {
    try {
      const input = {
        goalId,
        progress,
        notes: notes || '',
        updatedAt: new Date().toISOString(),
      };

      await updateGoalProgress(input);
      setSuccessMessage(t('Progress updated successfully!'));

      if (selectedGoal?.id === goalId) {
        setSelectedGoal(prev => prev ? { ...prev, progress } : null);
      }

      setRefetchTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error updating progress:', err);
      setError(getErrorMessage(err, t('Failed to update progress')));
      throw err;
    }
  };

  const handleFilterChange = (newFilters: Filters): void => {
    setFilters(newFilters);
  };

  const handleToggleSection = (section: keyof ExpandedSections, isExpanded: boolean): void => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: isExpanded
    }));
  };

  const handleViewToggle = (): void => {
    setViewType(prev => prev === 'map' ? 'list' : 'map');
  };

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const handleCloseSuccess = useCallback(() => {
    setSuccessMessage(null);
  }, []);

  // ----------------------
  // RETURN
  // ----------------------

  return {
    // State
    isFilterPanelOpen,
    setIsFilterPanelOpen,
    selectedGoal,
    isModalOpen,
    setIsModalOpen,
    modalMode,
    hoveredGoal,
    setHoveredGoal,
    expandedSections,
    viewType,
    filters,
    expandedNodes,
    loading,
    detailLoading,
    savingGoal,
    stats,
    error,
    successMessage,
    departments,
    loadingDepartments,
    availableOwners,
    loadingOwners,
    childLimits,

    // Computed
    allGoalsFlat,
    mapGoals,
    summaryStats,
    filtersActive,

    // Functions
    buildTreePositionMap,
    toggleNodeExpanded,
    showMoreChildren,
    applyStatusFilter,

    // Handlers
    handleGoalClick,
    openGoalById,
    handleCreateNewGoal,
    handleSaveGoal,
    handleDeleteGoal,
    handleUpdateProgress,
    handleFilterChange,
    handleToggleSection,
    handleViewToggle,
    handleCloseError,
    handleCloseSuccess,
  };
}
